import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useSportsLang } from '../contexts/SportsLangContext';
import { VUKZ, VUKZ_GRADIENT } from '../theme/vukzTheme';
import PageHeader from '../components/layout/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import {
  getAthleteRankingSummary,
  getVukzRankings,
  getMySwimmingResults,
  buildEventKey,
  parseEventLabel,
} from '../services/vukzRankingsService';
import { getEvents } from '../services/swimmingService';
import {
  Trophy, Timer, Medal, TrendingUp, ChevronDown,
  User, Building2, Filter, BarChart3, Target,
  Calendar, Award, Waves, ArrowUpRight, ArrowDownRight, Minus,
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════
   AthletePerformancePage — Minha Performance (Atleta)
   Route: /my/performance
   Widgets:
     1. Meus Rankings — all events with VUKZ/BR/USA/AUS positions
     2. Meu Tempo vs Ranking — selected event, athlete's time vs top
     3. Meus Resultados — competition results history
   ═══════════════════════════════════════════════════════════════ */

const LABELS = {
  pt: {
    title: 'Minha Performance',
    subtitle: (name) => `Seus tempos, rankings e evolução · ${name}`,
    widget1Title: 'Meus Rankings',
    widget1Sub: (y) => `Todas as provas que você competiu em ${y}`,
    cols1: ['Prova', 'Melhor Tempo', 'VUKZ', 'Brasil', 'EUA', 'Austrália'],
    noResults: (y) => `Nenhum resultado encontrado em ${y}`,
    you: 'Você',
    widget2Title: 'Meu Tempo vs Ranking',
    selectEvent: 'Selecione uma prova acima',
    eventPlaceholder: 'Selecione a prova',
    selectHint: 'Selecione uma prova na tabela acima para comparar',
    noRanking: 'Nenhum dado de ranking para esta prova',
    cols2: ['#', 'Atleta', 'País', 'Tempo', 'Diferença'],
    myTime: 'MEU TEMPO',
    widget3Title: 'Meus Resultados',
    widget3Sub: (y) => `Resultados em competições · ${y}`,
    cols3: ['Prova', 'Tempo', 'Colocação', 'Fase', 'Competição', 'Clube'],
  },
  en: {
    title: 'My Performance',
    subtitle: (name) => `Your times, rankings and progress · ${name}`,
    widget1Title: 'My Rankings',
    widget1Sub: (y) => `All events you competed in ${y}`,
    cols1: ['Event', 'Best Time', 'VUKZ', 'Brazil', 'USA', 'Australia'],
    noResults: (y) => `No results found in ${y}`,
    you: 'You',
    widget2Title: 'My Time vs Ranking',
    selectEvent: 'Select an event above',
    eventPlaceholder: 'Select event',
    selectHint: 'Select an event in the table above to compare',
    noRanking: 'No ranking data for this event',
    cols2: ['#', 'Athlete', 'Country', 'Time', 'Diff'],
    myTime: 'MY TIME',
    widget3Title: 'My Results',
    widget3Sub: (y) => `Competition results · ${y}`,
    cols3: ['Event', 'Time', 'Place', 'Phase', 'Competition', 'Club'],
  },
};

const CURRENT_YEAR = new Date().getFullYear();
const AVAILABLE_YEARS = [2025, 2024, 2023];

// ── Reusable Components ──────────────────────────────────────

const SelectPill = ({ value, onChange, options, icon: Icon, placeholder }) => (
  <div className="relative">
    <div className="flex items-center gap-1.5 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
      style={{ color: VUKZ.textLight }}>
      {Icon && <Icon size={13} />}
    </div>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="appearance-none pl-8 pr-8 py-2 rounded-xl text-xs font-bold border bg-white cursor-pointer hover:border-gray-300 focus:outline-none focus:ring-2 transition-all"
      style={{ borderColor: VUKZ.border, color: VUKZ.text }}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
    <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
      style={{ color: VUKZ.textLight }} />
  </div>
);

const WidgetCard = ({ icon: Icon, title, subtitle, filters, children, className = '' }) => (
  <div className={`bg-white rounded-2xl border overflow-hidden ${className}`} style={{ borderColor: VUKZ.border }}>
    <div className="px-5 pt-5 pb-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: VUKZ_GRADIENT }}>
            <Icon size={18} color="white" />
          </div>
          <div>
            <h2 className="text-sm font-extrabold" style={{ color: VUKZ.text }}>{title}</h2>
            {subtitle && (
              <p className="text-[10px]" style={{ color: VUKZ.textLight }}>{subtitle}</p>
            )}
          </div>
        </div>
      </div>
      {filters && (
        <div className="flex flex-wrap items-center gap-2">{filters}</div>
      )}
    </div>
    <div className="px-5 pb-5">{children}</div>
  </div>
);

const SkeletonRows = ({ count = 4 }) => (
  <div className="space-y-2">
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="h-12 rounded-xl animate-pulse" style={{ backgroundColor: VUKZ.primaryLight }} />
    ))}
  </div>
);

const EmptyState = ({ icon: Icon, message }) => (
  <div className="text-center py-10">
    <div className="w-12 h-12 rounded-2xl mx-auto mb-3 flex items-center justify-center"
      style={{ backgroundColor: VUKZ.primaryLight }}>
      <Icon size={20} style={{ color: VUKZ.textLight }} />
    </div>
    <p className="text-xs font-medium" style={{ color: VUKZ.textLight }}>{message}</p>
  </div>
);

const RankBadge = ({ rank, scope }) => {
  if (!rank) return <span className="text-[10px]" style={{ color: VUKZ.textLight }}>—</span>;
  const colors = {
    vukz: { bg: VUKZ.primary, text: 'white' },
    br: { bg: '#16a34a', text: 'white' },
    usa: { bg: '#2563eb', text: 'white' },
    aus: { bg: '#f59e0b', text: 'white' },
  };
  const c = colors[scope] || colors.vukz;
  return (
    <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-md inline-block"
      style={{ backgroundColor: `${c.bg}18`, color: c.bg }}>
      #{rank}
    </span>
  );
};

// ── Main Page ────────────────────────────────────────────────

const AthletePerformancePage = () => {
  const { user } = useAuth();

  // Year selector
  const [year, setYear] = useState(String(CURRENT_YEAR));

  // Widget 1: Ranking Summary
  const [rankingSummary, setRankingSummary] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(true);

  // Widget 2: Meu Tempo vs Ranking
  const [selectedEventKey, setSelectedEventKey] = useState('');
  const [comparisonData, setComparisonData] = useState([]);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [myBestTime, setMyBestTime] = useState(null);

  // Widget 3: Meus Resultados
  const [myResults, setMyResults] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(true);

  // Athlete ID (from user context — the user's ID is also the swimmer_id in VUKZ system)
  const athleteId = user?.id;
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;

  // ── Fetch ranking summary ──
  const fetchSummary = useCallback(async () => {
    if (!athleteId) return;
    setSummaryLoading(true);
    try {
      const res = await getAthleteRankingSummary(athleteId, Number(year));
      const items = res?.items || [];
      setRankingSummary(items);
      // Auto-select first event for comparison
      if (items.length > 0 && !selectedEventKey) {
        setSelectedEventKey(items[0].event_key);
      }
    } catch (err) {
      console.error('Error fetching ranking summary:', err);
      setRankingSummary([]);
    } finally {
      setSummaryLoading(false);
    }
  }, [athleteId, year, selectedEventKey]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  // ── Fetch comparison (my time vs ranking) ──
  const fetchComparison = useCallback(async () => {
    if (!selectedEventKey || !year) return;
    setComparisonLoading(true);
    try {
      const res = await getVukzRankings({
        year: Number(year),
        event_key: selectedEventKey,
        scope: 'vukz',
        limit: 20,
      });
      const items = res?.items || [];
      setComparisonData(items);

      // Find my best time from ranking summary
      const myEvent = rankingSummary.find((e) => e.event_key === selectedEventKey);
      setMyBestTime(myEvent || null);
    } catch (err) {
      console.error('Error fetching comparison:', err);
      setComparisonData([]);
    } finally {
      setComparisonLoading(false);
    }
  }, [selectedEventKey, year, rankingSummary]);

  useEffect(() => { fetchComparison(); }, [fetchComparison]);

  // ── Fetch my results ──
  const fetchResults = useCallback(async () => {
    setResultsLoading(true);
    try {
      const fromDate = `${year}-01-01`;
      const toDate = `${year}-12-31`;
      const res = await getMySwimmingResults({ from: fromDate, to: toDate, limit: 200 });
      setMyResults(res?.results || []);
    } catch (err) {
      console.error('Error fetching my results:', err);
      setMyResults([]);
    } finally {
      setResultsLoading(false);
    }
  }, [year]);

  useEffect(() => { fetchResults(); }, [fetchResults]);

  // ── Reset event when year changes ──
  const handleYearChange = (y) => {
    setYear(y);
    setSelectedEventKey('');
  };

  // ── Render ──
  return (
    <div className="min-h-screen" style={{ backgroundColor: VUKZ.bg }}>
      <div className="max-w-6xl mx-auto px-4 lg:px-6 py-6">

        {/* ═══ Page Header ═══ */}
        <PageHeader
          title={t.title}
          subtitle={t.subtitle(user?.name || (lang === 'en' ? 'Athlete' : 'Atleta'))}
          badgeText="PERFORMANCE"
          rightSlot={
            <SelectPill
              value={year}
              onChange={handleYearChange}
              icon={Calendar}
              options={AVAILABLE_YEARS.map((y) => ({ value: String(y), label: String(y) }))}
            />
          }
          className="mb-6"
        />

        <div className="space-y-6">

          {/* ═══ Widget 1: Meus Rankings ═══ */}
          <WidgetCard
            icon={Trophy}
            title={t.widget1Title}
            subtitle={t.widget1Sub(year)}
          >
            {summaryLoading ? (
              <SkeletonRows count={5} />
            ) : rankingSummary.length === 0 ? (
              <EmptyState icon={Trophy} message={t.noResults(year)} />
            ) : (
              <div className="overflow-x-auto -mx-5">
                <table className="w-full min-w-[600px]">
                  <thead>
                    <tr className="border-b" style={{ borderColor: VUKZ.border }}>
                      {t.cols1.map((h) => (
                        <th key={h} className="text-left px-5 py-2.5 text-[10px] font-bold uppercase tracking-wider"
                          style={{ color: VUKZ.textLight }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rankingSummary.map((item) => (
                      <tr key={item.event_key}
                        className={`border-b hover:bg-gray-50/50 transition-colors cursor-pointer ${selectedEventKey === item.event_key ? 'bg-gray-50' : ''}`}
                        style={{ borderColor: VUKZ.border }}
                        onClick={() => setSelectedEventKey(item.event_key)}
                      >
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <Waves size={14} style={{ color: VUKZ.primary }} />
                            <span className="text-xs font-bold" style={{ color: VUKZ.text }}>
                              {item.event_label || parseEventLabel(item.event_key)}
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-3">
                          <span className="text-xs font-extrabold font-mono" style={{ color: VUKZ.primary }}>
                            {item.best_value || '—'}
                          </span>
                        </td>
                        <td className="px-5 py-3"><RankBadge rank={item.rank_vukz} scope="vukz" /></td>
                        <td className="px-5 py-3"><RankBadge rank={item.rank_br} scope="br" /></td>
                        <td className="px-5 py-3"><RankBadge rank={item.rank_usa} scope="usa" /></td>
                        <td className="px-5 py-3"><RankBadge rank={item.rank_aus} scope="aus" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </WidgetCard>

          {/* ═══ Widget 2: Meu Tempo vs Ranking ═══ */}
          <WidgetCard
            icon={BarChart3}
            title={t.widget2Title}
            subtitle={selectedEventKey
              ? `${parseEventLabel(selectedEventKey)} · Top 20 VUKZ ${year}`
              : t.selectEvent}
            filters={
              rankingSummary.length > 0 ? (
                <SelectPill
                  value={selectedEventKey}
                  onChange={setSelectedEventKey}
                  icon={Filter}
                  placeholder={t.eventPlaceholder}
                  options={rankingSummary.map((e) => ({
                    value: e.event_key,
                    label: e.event_label || parseEventLabel(e.event_key),
                  }))}
                />
              ) : null
            }
          >
            {comparisonLoading ? (
              <SkeletonRows count={8} />
            ) : !selectedEventKey ? (
              <EmptyState icon={Target} message={t.selectHint} />
            ) : comparisonData.length === 0 ? (
              <EmptyState icon={Target} message={t.noRanking} />
            ) : (
              <div className="overflow-x-auto -mx-5">
                <table className="w-full min-w-[550px]">
                  <thead>
                    <tr className="border-b" style={{ borderColor: VUKZ.border }}>
                      {t.cols2.map((h) => (
                        <th key={h} className="text-left px-5 py-2.5 text-[10px] font-bold uppercase tracking-wider"
                          style={{ color: VUKZ.textLight }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonData.map((r, idx) => {
                      const isMe = r.athlete_id === athleteId;
                      const myTime = myBestTime?.best_seconds;
                      const diff = myTime && r.result_seconds
                        ? (r.result_seconds - myTime)
                        : null;

                      return (
                        <tr key={`${r.athlete_id}-${idx}`}
                          className={`border-b transition-colors ${isMe ? 'bg-gray-50' : 'hover:bg-gray-50/50'}`}
                          style={{
                            borderColor: VUKZ.border,
                            ...(isMe && { borderLeft: `3px solid ${VUKZ.primary}` }),
                          }}
                        >
                          <td className="px-5 py-3">
                            {r.rank <= 3 ? (
                              <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                                style={{
                                  backgroundColor: `${['#FFD700', '#C0C0C0', '#CD7F32'][r.rank - 1]}20`,
                                  color: ['#FFD700', '#C0C0C0', '#CD7F32'][r.rank - 1],
                                }}>
                                <Medal size={14} />
                              </div>
                            ) : (
                              <span className="text-xs font-extrabold" style={{ color: VUKZ.textLight }}>
                                {r.rank}
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                                style={{ backgroundColor: isMe ? VUKZ.primary : VUKZ.primaryLight }}>
                                <User size={12} style={{ color: isMe ? 'white' : VUKZ.primary }} />
                              </div>
                              <span className={`text-xs font-bold ${isMe ? 'underline' : ''}`}
                                style={{ color: VUKZ.text }}>
                                {isMe ? `${r.athlete_name} (${t.you})` : r.athlete_name}
                              </span>
                            </div>
                          </td>
                          <td className="px-5 py-3">
                            <span className="text-[10px]" style={{ color: VUKZ.textLight }}>
                              {r.country || '—'}
                            </span>
                          </td>
                          <td className="px-5 py-3">
                            <span className="text-xs font-extrabold font-mono"
                              style={{ color: isMe ? VUKZ.primary : VUKZ.text }}>
                              {r.result_value || '—'}
                            </span>
                          </td>
                          <td className="px-5 py-3">
                            {diff !== null && !isMe ? (
                              <span className={`text-[10px] font-bold flex items-center gap-0.5`}
                                style={{ color: diff < 0 ? VUKZ.success : diff > 0 ? VUKZ.error : VUKZ.textLight }}>
                                {diff < 0 ? <ArrowDownRight size={10} /> : diff > 0 ? <ArrowUpRight size={10} /> : <Minus size={10} />}
                                {Math.abs(diff).toFixed(2)}s
                              </span>
                            ) : isMe ? (
                              <span className="text-[10px] font-bold" style={{ color: VUKZ.primary }}>{t.myTime}</span>
                            ) : (
                              <span className="text-[10px]" style={{ color: VUKZ.textLight }}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </WidgetCard>

          {/* ═══ Widget 3: Meus Resultados ═══ */}
          <WidgetCard
            icon={Award}
            title={t.widget3Title}
            subtitle={t.widget3Sub(year)}
          >
            {resultsLoading ? (
              <SkeletonRows count={5} />
            ) : myResults.length === 0 ? (
              <EmptyState icon={Award} message={t.noResults(year)} />
            ) : (
              <div className="overflow-x-auto -mx-5">
                <table className="w-full min-w-[650px]">
                  <thead>
                    <tr className="border-b" style={{ borderColor: VUKZ.border }}>
                      {t.cols3.map((h) => (
                        <th key={h} className="text-left px-5 py-2.5 text-[10px] font-bold uppercase tracking-wider"
                          style={{ color: VUKZ.textLight }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {myResults.map((r, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50/50 transition-colors"
                        style={{ borderColor: VUKZ.border }}>
                        <td className="px-5 py-3">
                          <span className="text-xs font-bold" style={{ color: VUKZ.text }}>
                            {r.event_name || `${r.distance_meters}m ${r.stroke}`}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          <span className="text-xs font-extrabold font-mono" style={{ color: VUKZ.primary }}>
                            {r.time_formatted || r.time_seconds?.toFixed(2) || '—'}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          {r.position && r.position <= 3 ? (
                            <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                              style={{
                                backgroundColor: `${['#FFD700', '#C0C0C0', '#CD7F32'][r.position - 1]}20`,
                                color: ['#FFD700', '#C0C0C0', '#CD7F32'][r.position - 1],
                              }}>
                              <Medal size={14} />
                            </div>
                          ) : (
                            <span className="text-xs font-bold" style={{ color: VUKZ.textLight }}>
                              {r.position || '—'}
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-3">
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-md"
                            style={{ backgroundColor: VUKZ.primaryLight, color: VUKZ.primary }}>
                            {r.phase || r.fase || '—'}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          <span className="text-xs" style={{ color: VUKZ.textLight }}>
                            {r.competition_name || '—'}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-1.5">
                            <Building2 size={12} style={{ color: VUKZ.textLight }} />
                            <span className="text-xs" style={{ color: VUKZ.text }}>
                              {r.club_display_name || r.club_name || '—'}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </WidgetCard>

        </div>
      </div>
    </div>
  );
};

export default AthletePerformancePage;
